import { MRT_ColumnDef } from 'mantine-react-table'
import { Avatar, Text, Tooltip } from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { ReactNode } from 'react'
import { DealParams } from 'app/models/deal-params'
import getInitials from 'app/views/components/functions/getInitials'
import DealActions from '../deal-details/deal-actions'
import { formatDateToShow, formatDateToUTC, formatNumberToCurrency, highlightToday, stringToDate } from 'app/utils/util-functions'
import { onSameDay, timestampToDate } from 'app/utils/grid-date-filter'

function TableHead({ header }: { header: string }) {
  return (
    <Text
      style={{
        fontFamily: 'Plus Jakarta Sans',
        fontSize: '1.5em',
      }}
    >
      {header}
    </Text>
  )
}

function TableRowText({ text }: { text: ReactNode }) {
  return (
    <Text
      style={{
        fontFamily: 'Plus Jakarta Sans',
        fontSize: '1.5em',
      }}
    >
      {text}
    </Text>
  )
}
/**
 * The Terminated deals grid's columns. Its rows come from the Deals read
 * (`GET /api/deal-management/screen`), which carries the borrower and admin
 * names and when the deal was terminated, so no entity list is needed here.
 */
export function getLockedColumnDefs(): MRT_ColumnDef<DealParams>[] {
  return [
    {
      size: 40,
      accessorKey: 'dealName',
      header: 'Deal Name',
      Header: <TableHead header={'Deal Name'} />,
      Cell: ({ cell }) => (
        <TableRowText text={cell.getValue<string>()} />
      ),
    },
    {
      size: 40,
      // when the deal was locked; a locked deal carries no amendment date
      accessorKey: 'terminationDate',
      accessorFn: (row: any) => timestampToDate(row.terminationDate),
      header: 'Termination Date',
      Header: <TableHead header={'Termination Date'} />,
      filterFn: onSameDay,
      sortingFn: 'datetime',
      Filter: ({ column }) => (
        <DateInput
          valueFormat='DD-MMM-YYYY'
          placeholder="Filter by Start Date"
          firstDayOfWeek={0}
          onChange={(newValue: Date) => {
            column.setFilterValue(newValue)
          }}
          value={column.getFilterValue() as Date}
          getDayProps={highlightToday()}
        />
      ),
      Cell: ({ cell }) => {
        const value = cell.getValue<Date | null>()
        return <Text fz="1.5em">{value ? formatDateToUTC(value) : ''}</Text>
      },
    },
    {
      size: 50,
      accessorKey: 'amount',
      header: 'Amount',
      Header: <TableHead header={'Amount'} />,
      filterVariant: 'range',
      Cell: ({ cell, row }: any) => (
        <TableRowText
          text={formatNumberToCurrency(
            cell.getValue(),
            row.original.currency ?? 'USD'
          )}
        />
      ),
    },
    {
      size: 80,
      accessorKey: 'startDate',
      header: 'Start Date',
      Header: <TableHead header={'Start Date'} />,
      accessorFn: row => {
        return stringToDate(row.startDate);
      },
      mantineFilterTextInputProps: {
        placeholder: 'Date',
      },
      filterFn: 'lessThanOrEqualTo',
      filterVariant: 'date',
      sortingFn: 'datetime',
      Cell: ({ cell }) => (
        <Text fz="1.5em">{formatDateToShow(cell.getValue<Date>())}</Text>
      ), //render Date as a string
    },
    {
      size: 80,
      accessorKey: 'maturity',
      header: 'Maturity Date',
      Header: <TableHead header={'Maturity Date'} />,
      accessorFn: row => {
        return stringToDate(row.maturity);
      },
      mantineFilterTextInputProps: {
        placeholder: 'Date',
      },
      filterFn: 'greaterThanOrEqualTo',
      filterVariant: 'date',
      sortingFn: 'datetime',
      Cell: ({ cell }) => (
        <Text fz="1.5em">{formatDateToShow(cell.getValue<Date>())}</Text>
      ), //render Date as a string
    },
    {
      size: 50,
      // the primary borrower's name, resolved by the read
      accessorFn: (row: any) => row.borrowerNames?.at(0) ?? '',
      accessorKey: 'borrowers',
      header: 'Borrowers',
      Header: <TableHead header={'Primary Borrower'} />,
      Cell: ({ cell }) => {
        const entityName = cell.getValue<string>() ?? '';
        return (
          <Tooltip label={entityName}>
            <Avatar radius="xl" color="cyan" size="lg">
              {getInitials(entityName)}
            </Avatar>
          </Tooltip>
        )
      },
    },
    {
      size: 50,
      accessorKey: 'adminEntityName',
      header: 'Admin',
      Header: <TableHead header={'Admin'} />,
      Cell: ({ cell }) => {
        const value = cell.getValue();
        if (Array.isArray(value)) {
          return (
            <Avatar.Group spacing="sm">
              {value.map((adminEntity, i) => (
                <Tooltip key={i} label={adminEntity}>
                  <Avatar radius="xl" color="cyan" size="lg">
                    {getInitials(adminEntity)}
                  </Avatar>
                </Tooltip>
              ))}
            </Avatar.Group>
          );
        }
      },
    },
    {
      size: 40,
      accessorKey: 'id',
      header: 'Actions',
      Header: <TableHead header={'Actions'} />,
      enableColumnActions: false,
      enableColumnFilter: false,
      Cell: cell => <DealActions row={cell.row.original} />,
    },
  ]
}
